import { CSSProperties, useState } from "react";
import AudioPlayer from "components/shareable/AudioPlayer";
import Icon from "elements/Icon";
import useWebsiteBuilder, { MusicContentStyle, WebsiteBuilderContent, WebsiteBuilderStyle } from "Hooks/useWebsiteBuilder";
import { makeKey, styx } from "modules/Utils";
import VinylPlayer from "pages/WebsitePage/themes/VinylPlayer";
import PreviewContentEmptyState from "./PreviewContentEmptyState";
import DebugBox from "components/shareable/DebugBox";
import useScreen from "Hooks/useScreen";
import { whiteOrBlackTextColor } from "helpers/StyleUtils";

interface MusicPreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
}

const MusicPreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
}: MusicPreviewContentProps) => {
    const { mobileView } = useScreen();
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;

    const {
        backgroundColor: backgroundColorWebsiteStyle,
        textColor: OLD,
        fontFamily,
    } = websiteStyle;

    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled;
    const contentBackgroundColor = websiteContent[contentIndex].contentBackgroundColor;
    const backgroundColor = isContentBackgroundColorEnabled ? contentBackgroundColor || backgroundColorWebsiteStyle : backgroundColorWebsiteStyle;

    const isContentTextColorEnabled = websiteContent[contentIndex].isContentTextColorEnabled;
    const contentTextColor = websiteContent[contentIndex].contentTextColor;
    const textColor = isContentTextColorEnabled ? contentTextColor : whiteOrBlackTextColor(backgroundColor);

    const musicContent = websiteContent[contentIndex].musicContent;

    const {
        selectedMusic,
        style,
    } = musicContent || {};

    const {
        name,
        preview_url,
        primary_artist,
        record_url,
        thumbnail_url,
        spotify_release_type,
        tracks,
    } = selectedMusic || {};

    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [activeTrackIndex, setActiveTrackIndex] = useState<number>(0);

    const renderMusicPlayer = () => {
        if (style === MusicContentStyle.FULL_SCREEN && spotify_release_type?.toLowerCase() === 'single') {
            return null;
        }

        let musicPlayerTracks = [{
            name,
            preview_url,
            record_url,
        }]

        const isAlbumOrPlaylist = spotify_release_type?.toLowerCase() === 'album' || spotify_release_type?.toLowerCase() === 'playlist';

        if (isAlbumOrPlaylist) {
            musicPlayerTracks = tracks?.map((track) => ({
                name: track.name,
                preview_url: track.preview_url,
                record_url: track.record_url,
            })) || [];
        }

        // console.log('MusicPreviewContent - musicPlayerTracks >>>>', musicPlayerTracks);

        return (
            <div
                className="musicPreviewContentPlayerInnerContainer"
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 8,
                    color: textColor,
                }}
            >
                {musicPlayerTracks.map((track, index) => {
                    const {
                        name,
                        preview_url,
                        record_url,
                    } = track;

                    const isActiveTrack = activeTrackIndex === index;

                    return (
                        <div
                            key={makeKey(name, index, contentIndex)}
                            className="musicPreviewContentInfo"
                            style={styx(
                                sty.musicPreviewContentInfo,
                                {
                                    borderColor: `${textColor}50`,
                                }
                            )}
                        >
                            {musicPlayerTracks &&
                                <div
                                    style={{
                                        fontSize: 14,
                                        opacity: 0.5,
                                    }}
                                >
                                    {index + 1}.
                                </div>
                            }
                            {style === MusicContentStyle.PLAYER &&
                                <img
                                    src={thumbnail_url}
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 8,
                                    }}
                                />
                            }
                            <Icon
                                className="musicPreviewContentFullScreenPlayButton1"
                                isCircle
                                icon={isPlaying && isActiveTrack ? 'pause' : 'play'}
                                size={24}
                                color={textColor}
                                onClick={() => {
                                    if (isActiveTrack) {
                                        setIsPlaying(!isPlaying);
                                    } else {
                                        setActiveTrackIndex(index);
                                        setIsPlaying(true);
                                    }
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    borderWidth: 1,
                                    borderColor: `${textColor}50`,
                                    borderStyle: 'solid',
                                    padding: 4,
                                    opacity: 0.5,
                                }}
                            />
                            <div className="musicInfoInnerContainer" style={sty.musicInfoInnerContainer}>
                                <div
                                    className="musicPreviewContentTitle"
                                    style={styx(
                                        sty.musicPreviewContentTitle,
                                        { fontFamily }
                                    )}
                                >
                                    {name}
                                </div>
                                <div
                                    className="musicPreviewContentArtist"
                                    style={styx(sty.musicPreviewContentArtist)}
                                >
                                    <div style={{ fontFamily }}>{primary_artist}</div>
                                    {isActiveTrack &&
                                        <AudioPlayer
                                            url={preview_url || ''}
                                            playing={isPlaying}
                                            loop
                                            progressBarColor={textColor}
                                            backgroundContrast="dark"
                                        />
                                    }
                                </div>
                            </div>
                            <Icon
                                className="musicPreviewShareButton"
                                style={{
                                    opacity: 0.5,
                                }}
                                icon="externalLink"
                                size={24}
                                color={textColor}
                                onClick={() => window.open(record_url, '_blank')}
                            />
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderMusicContent = () => {
        if (!thumbnail_url) {
            return (
                <>
                    <PreviewContentEmptyState
                        className="musicPreviewContentImageArtwork"
                        title="Add music to your website"
                        icon="music"
                    />
                    {renderMusicPlayer()}
                </>
            )
        }
        switch (style) {
            case MusicContentStyle.ARTWORK:
                return (
                    <>
                        <div
                            className="musicPreviewContentImageArtwork"
                            style={{
                                width: '100%',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundImage: thumbnail_url ? `url(${thumbnail_url})` : undefined,
                                paddingBottom: '100%',
                                borderRadius: 16,
                            }}
                        />
                        {renderMusicPlayer()}
                    </>
                )
            case MusicContentStyle.FULL_SCREEN:
                return (
                    <>
                        <div
                            className="musicPreviewContentFullScreen"
                            style={{
                                width: '100%',
                                position: 'relative',
                            }}
                        >
                            <div
                                className="musicPreviewContentFullScreenImage"
                                style={{
                                    backgroundImage: thumbnail_url ? `url(${thumbnail_url})` : undefined,
                                    width: '100%',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    position: 'relative',
                                    paddingBottom: '120%',
                                    marginTop: contentIndex === 0 && !mobileView ? -38 : 0,
                                }}
                            />
                            <div
                                className="musicFullScreenGradientOverlay"
                                style={sty.musicFullScreenGradientOverlay}
                            />
                            <div
                                className="musicPreviewContentFullScreenContent"
                                style={styx(
                                    sty.musicPreviewContentFullScreenContent,
                                    {
                                        background: `linear-gradient(to bottom, transparent, ${backgroundColor})`,
                                        minHeight: 125,
                                    }
                                )}
                            >
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    <Icon
                                        className="musicPreviewContentFullScreenPlayButton2"
                                        isCircle
                                        icon={isPlaying ? 'pause' : 'play'}
                                        size={32}
                                        color={textColor}
                                        onClick={() => {
                                            setIsPlaying(!isPlaying);
                                        }}
                                        style={{
                                            backgroundColor: 'transparent',
                                            borderWidth: 1,
                                            borderColor: `${textColor}50`,
                                            borderStyle: 'solid',
                                            padding: 4,
                                            opacity: 0.5,
                                        }}
                                    />
                                    <div
                                        className="musicPreviewContentFullScreenContentInnerContainer"
                                        style={{
                                            color: textColor,
                                            fontFamily,
                                        }}
                                    >
                                        <div
                                            className="musicPreviewContentFullScreenContentArtist"
                                            style={styx(
                                                sty.musicPreviewContentFullScreenContentArtist,
                                            )}
                                        >
                                            {primary_artist}
                                        </div>
                                        <div
                                            className="musicPreviewContentFullScreenContentTitle"
                                            style={styx(
                                                sty.musicPreviewContentFullScreenContentTitle,
                                            )}
                                        >
                                            {name}
                                        </div>
                                    </div>
                                </div>
                                <AudioPlayer
                                    url={preview_url || ''}
                                    playing={isPlaying}
                                    loop
                                    progressBarColor={textColor}
                                    backgroundContrast="dark"
                                />
                            </div>
                        </div>

                        <div
                            className="musicPreviewContentFullScreenPlayer"
                            style={{
                                width: '100%',
                                paddingLeft: 16,
                                paddingRight: 16,
                            }}
                        >
                            {renderMusicPlayer()}
                        </div>
                    </>
                )
            case MusicContentStyle.PLAYER:
                return (
                    <div className="musicPreviewContentPlayer" style={{ width: '100%' }}>
                        {renderMusicPlayer()}
                    </div>
                )
            case MusicContentStyle.RECORD_PLAYER:
                return (
                    <>
                        <VinylPlayer
                            imageUrl={thumbnail_url || ''}
                            playing={isPlaying}
                        />
                        <div
                            className="musicPreviewContentPlayer"
                            style={{
                                paddingTop: 36,
                            }}
                        >
                            {renderMusicPlayer()}
                        </div>
                    </>
                )
            default:
                return null
        }
    }

    // NOTE: render()
    return (
        <div
            className="musicPreviewContentComponent"
            style={styx(
                sty.musicPreviewContentComponent,
                style !== MusicContentStyle.FULL_SCREEN && {
                    paddingLeft: 16,
                    paddingRight: 16,
                },
                styleProp,
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            {renderMusicContent()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    musicPreviewContentComponent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 8,
    },
    musicPreviewContentInfo: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        width: '100%',
    },
    musicPreviewContentArtist: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        opacity: 0.5,
    },

    musicPreviewContentFullScreenContent: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        width: '100%',
        gap: 8,
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        paddingLeft: 16,
        paddingRight: 16,
        paddingBottom: 16,
    },
    musicPreviewContentFullScreenContentTitle: {
        fontSize: 28,
    },
    musicPreviewContentFullScreenContentArtist: {
        fontSize: 18,
        opacity: 0.7,
        marginBottom: -4,
    },
    musicFullScreenGradientOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 100,
        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1))',
    },
    musicPreviewContentTitle: {
        width: '100%',
    },
    musicInfoInnerContainer: {
        width: '100%',
    }
}

export default MusicPreviewContent;